import { useState, type DragEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { canValidateWithImss } from '@domain/entities/Incapacity';
import { hourWindowMinutes } from '@domain/entities/PermissionRequest';
import { seedColorForSubArea } from '@domain/entities/Organization';
import type { IncapacityId, PermissionRequestId } from '@shared/brand';
import type { Incapacity } from '@domain/entities/Incapacity';
import type { PermissionRequest } from '@domain/entities/PermissionRequest';
import {
  AWAITING_INCAPACITY_STATUSES,
  CERTIFICATE_BOARD_COLUMNS,
  PERMIT_BOARD_COLUMNS,
  type IncapacityStatus,
  type PermissionRequestStatus,
} from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import { Avatar, Banner, Card, Chip, Spinner, cssVars, cx } from '../../design-system';
import { formatDateRange, formatHourWindow } from '../../format';
import { placeOf, useApprovalScope } from '../../hooks/useApprovalScope';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import {
  ObserveIncapacityDialog,
  RejectPermissionDialog,
  ReopenPermitDialog,
  decidePermission,
  validateIncapacity,
} from './absenceDecisions';
import styles from './ApprovalsScreen.module.css';
import { loadFailed } from '../../loadError';
import { stillOnPermitBoard } from './permitBoard';

/**
 * The permit and certificate boards.
 *
 * Same column layout as the vacation board so the screen reads as one thing, but
 * each type brings its own columns — their statuses genuinely differ, and a
 * shared set would mean columns that can never hold a card.
 *
 * Drag and drop works exactly as it does for vacations, and for the same reason it
 * is safe there: **a drop never applies a decision silently.** The vacation board
 * already routes a drop onto «Aprobada» through its approve call and a drop onto
 * «Rechazada» through the reason dialog, and these do the same — «Aprobado» decides,
 * «Rechazado» opens the catalogue, «Validada» validates, «Observada» asks for the
 * note. The callable re-checks all of it regardless of how the card got there.
 *
 * What is *not* offered is the half that has no transition behind it. Only a card
 * awaiting a decision is draggable, and only the columns that a decision can reach
 * accept a drop — nothing un-decides a permit, and cancelling one belongs to the
 * person who filed it. A column that cannot receive the card simply is not a drop
 * target, which says so without a message.
 */

/** The prototype's `INK` map: a readable text colour for each column tint. */
const INK: Readonly<Record<string, string>> = {
  '#7C5CFF': '#5B3FD4',
  '#E8930C': '#8A5A05',
  '#38619F': '#2A4E86',
  '#12A594': '#0B7466',
  '#E5484D': '#A82B30',
};

const inkFor = (color: string): string => INK[color] ?? color;

/** The one column a permit waits in. Everything else is an outcome. */
const DECIDABLE = 'Por aprobar';

const BoardShell = ({
  loading,
  failed,
  emptyLabel,
  children,
}: {
  loading: boolean;
  failed: boolean;
  emptyLabel: string;
  children: ReactNode;
}): ReactNode => {
  if (loading) {
    return (
      <Card>
        <Spinner label={emptyLabel} />
      </Card>
    );
  }
  if (failed) return <Banner tone="danger">No pudimos cargar el tablero.</Banner>;
  return <div className={styles.board}>{children}</div>;
};

// ---------------------------------------------------------------------------
// Permits
// ---------------------------------------------------------------------------

export const PermitBoard = ({ canDecide }: { canDecide: boolean }): ReactNode => {
  const session = useSession();
  const approval = useApprovalScope();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState<PermissionRequest | null>(null);
  const [reopening, setReopening] = useState<PermissionRequest | null>(null);
  const [draggingId, setDraggingId] = useState<PermissionRequestId | null>(null);
  const [overColumn, setOverColumn] = useState<PermissionRequestStatus | null>(null);
  const place = placeOf(approval.scope);
  const today = getServices().clock.today();

  const board = useQuery({
    queryKey: ['approvals', 'permits', place, session.personId],
    enabled: !approval.loading,
    queryFn: async () => {
      const result = await getServices().permissions.list(place, { limit: 100 });
      if (!result.ok) throw loadFailed('No pudimos cargar los permisos.', result.error);
      // The people this session approves, and never themselves — the card would
      // otherwise offer a leader an approve button on their own permit.
      return approval.mine(result.value.items);
    },
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['approvals'] });
    await queryClient.invalidateQueries({ queryKey: ['shell', 'badge'] });
  };

  const approve = async (permission: PermissionRequest): Promise<void> => {
    const ok = await decidePermission(permission, 'approve', null);
    if (!ok) {
      toast.bad('No se pudo aprobar el permiso.');
      return;
    }
    toast.ok(`${permission.folio} aprobado.`);
    await refresh();
  };

  // Sin lo decidido de días que ya pasaron. Ver `stillOnPermitBoard`.
  const cards = (board.data ?? []).filter((permit) => stillOnPermitBoard(permit, today));

  /**
   * A decided permit can be pulled back out of any outcome — approved, rejected or
   * cancelled — but only while it is still undoable. Past the start date the person
   * has begun the time off, and the card is a record of something that happened.
   */
  const reversible = (permission: PermissionRequest): boolean =>
    permission.status !== DECIDABLE && today.isBefore(permission.range.start);

  /*
   * Decidir sobre un permiso, tarjeta por tarjeta y no de golpe.
   *
   * `canDecide` era el permiso a secas, y para un líder bastaba: su cola ya viene
   * filtrada por vínculo y su propio permiso nunca aparece. Quien administra o
   * configura ve el área entera —`mine` no filtra nada ahí—, así que tenía su
   * propio permiso delante con el botón «Aprobar» encendido. Y el servidor se lo
   * dejaba: la comprobación de permisos sólo mira los vínculos cuando quien llama
   * es `lider`.
   */
  const decidable = (permission: PermissionRequest): boolean =>
    canDecide && permission.personId !== session.personId;

  const canDrag = (permission: PermissionRequest): boolean =>
    decidable(permission) && (permission.status === DECIDABLE || reversible(permission));

  /**
   * Where the card being dragged may land, which depends on where it came from: an
   * undecided permit goes to a decision, and a decided one goes back to the queue.
   * An approved one may also be revoked straight to «Rechazado» — taking something
   * away is safe in a way that granting it is not.
   */
  const dragged = draggingId === null ? null : (cards.find((p) => p.id === draggingId) ?? null);

  const accepts = (status: PermissionRequestStatus): boolean => {
    if (dragged === null || !decidable(dragged) || dragged.status === status) return false;
    if (dragged.status === DECIDABLE) return status === 'Aprobado' || status === 'Rechazado';
    if (dragged.status === 'Aprobado') return status === DECIDABLE || status === 'Rechazado';
    // Rejected and cancelled only go back to the queue.
    return status === DECIDABLE;
  };

  const onDrop = async (status: PermissionRequestStatus): Promise<void> => {
    setOverColumn(null);
    const permission = dragged;
    setDraggingId(null);
    if (permission === null || permission.status === status) return;

    if (status === 'Rechazado') {
      setRejecting(permission);
      return;
    }
    // Back to the queue: the decision is cleared, so it asks for a reason too.
    if (status === DECIDABLE) {
      setReopening(permission);
      return;
    }
    if (status === 'Aprobado') await approve(permission);
  };

  return (
    <>
      <BoardShell
        loading={board.isLoading}
        failed={board.error !== null}
        emptyLabel="Cargando los permisos"
      >
        {PERMIT_BOARD_COLUMNS.map((column) => {
          const columnCards = cards.filter((p) => p.status === column.status);
          const open = accepts(column.status);
          const over = overColumn === column.status;
          return (
            <section
              key={column.status}
              className={cx(styles.column, over && styles.columnOver)}
              style={cssVars({
                '--column-color': column.color,
                '--column-ink': inkFor(column.color),
              })}
              onDragOver={
                open
                  ? (event: DragEvent) => {
                      event.preventDefault();
                      setOverColumn(column.status);
                    }
                  : undefined
              }
              onDragLeave={
                open
                  ? () => {
                      setOverColumn((current) =>
                        current === column.status ? null : current,
                      );
                    }
                  : undefined
              }
              onDrop={
                open
                  ? (event: DragEvent) => {
                      event.preventDefault();
                      void onDrop(column.status);
                    }
                  : undefined
              }
            >
              <header className={styles.columnHeader}>
                <span className={styles.columnDot} aria-hidden="true" />
                <span className={styles.columnTitle}>{column.status}</span>
                <span className={styles.columnCount}>{columnCards.length}</span>
              </header>

              <div className={styles.cards}>
                {columnCards.map((permission) => {
                  const minutes =
                    permission.hours === null ? 0 : hourWindowMinutes(permission.hours);
                  return (
                    <article
                      key={permission.id}
                      className={cx(
                        styles.card,
                        permission.status !== 'Por aprobar' && styles.cardDecided,
                        draggingId === permission.id && styles.cardDragging,
                      )}
                      draggable={canDrag(permission)}
                      onDragStart={() => {
                        setDraggingId(permission.id);
                      }}
                      onDragEnd={() => {
                        setDraggingId(null);
                        setOverColumn(null);
                      }}
                    >
                      <div className={styles.cardTop}>
                        <Avatar
                          name={permission.personName}
                          color={seedColorForSubArea(permission.subAreaName)}
                          size={34}
                        />
                        <div className={styles.cardWho}>
                          <div className={styles.cardName}>{permission.personName}</div>
                          <div className={styles.cardSub}>{permission.subAreaName}</div>
                        </div>
                        <span className={styles.cardCode}>{permission.folio}</span>
                      </div>

                      <div className={styles.cardRange}>
                        {permission.hours === null
                          ? formatDateRange(permission.range)
                          : `${formatDateRange(permission.range)} · ${formatHourWindow(
                              permission.hours,
                            )}`}
                      </div>
                      <div className={styles.cardDays}>
                        {/* Sin regreso no hay duración que poner junto al tipo:
                            el rango de arriba ya dice «sin regreso». */}
                        {permission.hours === null || permission.hours.to === null
                          ? permission.kind
                          : `${permission.kind} · ${(minutes / 60).toLocaleString('es-MX', {
                              maximumFractionDigits: 1,
                            })} h`}
                      </div>

                      <div className={styles.cardTags}>
                        <Chip
                          background={
                            permission.withPay
                              ? 'var(--color-success-bg)'
                              : 'var(--color-surface-subtle-alt)'
                          }
                          foreground={
                            permission.withPay
                              ? 'var(--color-success-ink)'
                              : 'var(--color-text-tertiary-alt)'
                          }
                        >
                          {permission.withPay ? 'Con goce' : 'Sin goce'}
                        </Chip>
                      </div>

                      {permission.reason.length > 0 && (
                        <p className={styles.motive}>{permission.reason}</p>
                      )}
                      {permission.resolutionReason !== null && (
                        <p className={styles.motive}>{permission.resolutionReason}</p>
                      )}

                      {permission.personId === session.personId &&
                        permission.status === 'Por aprobar' && (
                          <p className={styles.ownNote}>
                            Es tu permiso: lo decide alguien más.
                          </p>
                        )}

                      {decidable(permission) && permission.status === 'Por aprobar' && (
                        <div className={styles.cardFooter}>
                          <button
                            type="button"
                            className={styles.decideApprove}
                            onClick={() => {
                              void approve(permission);
                            }}
                          >
                            Aprobar
                          </button>
                          <button
                            type="button"
                            className={styles.decideReject}
                            onClick={() => {
                              setRejecting(permission);
                            }}
                          >
                            Rechazar
                          </button>
                        </div>
                      )}
                    </article>
                  );
                })}

                {/* No drop zone: nothing here drags, so an "arrastra una tarjeta"
                    hint would promise an interaction that does not exist. The empty
                    state still earns its place — a blank column reads as broken. */}
                {(open || columnCards.length === 0) && (
                  <div className={cx(styles.dropZone, over && styles.dropZoneOver)}>
                    {over ? 'Suelta aquí' : open ? 'Arrastra una tarjeta' : 'Sin permisos'}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </BoardShell>

      {reopening !== null && (
        <ReopenPermitDialog
          permission={reopening}
          onClose={() => {
            setReopening(null);
          }}
          onDone={async () => {
            setReopening(null);
            await refresh();
          }}
        />
      )}

      {rejecting !== null && (
        <RejectPermissionDialog
          permission={rejecting}
          onClose={() => {
            setRejecting(null);
          }}
          onDone={async () => {
            setRejecting(null);
            await refresh();
          }}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Certificates
// ---------------------------------------------------------------------------

export const CertificateBoard = ({ canValidate }: { canValidate: boolean }): ReactNode => {
  const approval = useApprovalScope();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [observing, setObserving] = useState<Incapacity | null>(null);
  const [draggingId, setDraggingId] = useState<IncapacityId | null>(null);
  const [overColumn, setOverColumn] = useState<IncapacityStatus | null>(null);
  const place = placeOf(approval.scope);

  const board = useQuery({
    queryKey: ['approvals', 'certificates', place],
    queryFn: async () => {
      const result = await getServices().incapacities.list(place, { limit: 100 });
      if (!result.ok) throw loadFailed('No pudimos cargar las incapacidades.', result.error);
      return result.value.items;
    },
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['approvals'] });
    await queryClient.invalidateQueries({ queryKey: ['shell', 'badge'] });
  };

  /*
   * «Validar» aquí es **RR.HH. aceptó el certificado**, no «el IMSS lo confirmó».
   *
   * El portal no consulta al instituto: no hay camino para hacerlo desde aquí — el
   * canal del patrón es el IDSE, con el certificado digital de la empresa y pensado
   * para sistemas de nómina. Los dos avisos decían lo contrario: uno culpaba al IMSS
   * de un fallo que era nuestro, y el otro daba por registrado un subsidio que este
   * portal no registra en ninguna parte.
   */
  const validate = async (incapacity: Incapacity): Promise<void> => {
    const ok = await validateIncapacity(incapacity);
    if (!ok) {
      toast.bad('No se pudo marcar como validada. Intenta de nuevo.');
      return;
    }
    toast.ok(`${incapacity.folio} validada: diste por bueno el certificado.`);
    await refresh();
  };

  const cards = board.data ?? [];

  /** The two columns a decision can actually reach. «Programada» is set by the
   *  leave itself starting, not by anybody deciding. */
  const accepts = (status: IncapacityStatus): boolean =>
    canValidate && (status === 'Validada' || status === 'Observada');

  const onDrop = async (status: IncapacityStatus): Promise<void> => {
    setOverColumn(null);
    const id = draggingId;
    setDraggingId(null);
    if (id === null) return;

    const incapacity = cards.find((i) => i.id === id);
    if (incapacity === undefined) return;

    if (status === 'Observada') {
      setObserving(incapacity);
      return;
    }
    if (status !== 'Validada') return;

    // The same precondition the button carries, and the server's: a certificate
    // with no folio has nothing to validate against.
    if (!canValidateWithImss(incapacity)) {
      toast.warn('Falta el folio del certificado para poder validar.');
      return;
    }
    await validate(incapacity);
  };

  return (
    <>
      <BoardShell
        loading={board.isLoading}
        failed={board.error !== null}
        emptyLabel="Cargando las incapacidades"
      >
        {CERTIFICATE_BOARD_COLUMNS.map((column) => {
          const columnCards = cards.filter((i) => i.status === column.status);
          const open = accepts(column.status);
          const over = overColumn === column.status;
          return (
            <section
              key={column.status}
              className={cx(styles.column, over && styles.columnOver)}
              style={cssVars({
                '--column-color': column.color,
                '--column-ink': inkFor(column.color),
              })}
              onDragOver={
                open
                  ? (event: DragEvent) => {
                      event.preventDefault();
                      setOverColumn(column.status);
                    }
                  : undefined
              }
              onDragLeave={
                open
                  ? () => {
                      setOverColumn((current) =>
                        current === column.status ? null : current,
                      );
                    }
                  : undefined
              }
              onDrop={
                open
                  ? (event: DragEvent) => {
                      event.preventDefault();
                      void onDrop(column.status);
                    }
                  : undefined
              }
            >
              <header className={styles.columnHeader}>
                <span className={styles.columnDot} aria-hidden="true" />
                <span className={styles.columnTitle}>{column.status}</span>
                <span className={styles.columnCount}>{columnCards.length}</span>
              </header>

              <div className={styles.cards}>
                {columnCards.map((incapacity) => (
                  <article
                    key={incapacity.id}
                    className={cx(
                      styles.card,
                      !AWAITING_INCAPACITY_STATUSES.includes(incapacity.status) &&
                        styles.cardDecided,
                      draggingId === incapacity.id && styles.cardDragging,
                    )}
                    draggable={canValidate && incapacity.status !== 'Validada'}
                    onDragStart={() => {
                      setDraggingId(incapacity.id);
                    }}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setOverColumn(null);
                    }}
                  >
                    <div className={styles.cardTop}>
                      <Avatar
                        name={incapacity.personName}
                        color={seedColorForSubArea(incapacity.subAreaName)}
                        size={34}
                      />
                      <div className={styles.cardWho}>
                        <div className={styles.cardName}>{incapacity.personName}</div>
                        <div className={styles.cardSub}>{incapacity.subAreaName}</div>
                      </div>
                      <span className={styles.cardCode}>{incapacity.folio}</span>
                    </div>

                    <div className={styles.cardRange}>{formatDateRange(incapacity.range)}</div>
                    <div className={styles.cardDays}>
                      {incapacity.naturalDays}{' '}
                      {incapacity.naturalDays === 1 ? 'día natural' : 'días naturales'} ·{' '}
                      {incapacity.kind}
                    </div>

                    <div className={styles.cardTags}>
                      <Chip
                        background="var(--color-warning-bg)"
                        foreground="var(--color-warning-ink)"
                      >
                        {incapacity.subsidy}
                      </Chip>
                      {incapacity.imssFolio.length > 0 && (
                        <Chip
                          background="var(--color-surface-subtle-alt)"
                          foreground="var(--color-text-tertiary-alt)"
                        >
                          {incapacity.umf.length > 0
                            ? `UMF ${incapacity.umf}`
                            : incapacity.imssFolio}
                        </Chip>
                      )}
                    </div>

                    {incapacity.observation !== null && (
                      <p className={styles.motive}>{incapacity.observation}</p>
                    )}

                    {canValidate && incapacity.status !== 'Validada' && (
                      <div className={styles.cardFooter}>
                        <button
                          type="button"
                          className={styles.decideApprove}
                          disabled={!canValidateWithImss(incapacity)}
                          title={
                            canValidateWithImss(incapacity)
                              ? undefined
                              : 'Falta el folio del certificado para poder validar.'
                          }
                          onClick={() => {
                            void validate(incapacity);
                          }}
                        >
                          Validar con IMSS
                        </button>
                        <button
                          type="button"
                          className={styles.decideReject}
                          onClick={() => {
                            setObserving(incapacity);
                          }}
                        >
                          Observar
                        </button>
                      </div>
                    )}
                  </article>
                ))}

                {/* No drop zone: nothing here drags, so an "arrastra una tarjeta"
                    hint would promise an interaction that does not exist. The empty
                    state still earns its place — a blank column reads as broken. */}
                {(open || columnCards.length === 0) && (
                  <div className={cx(styles.dropZone, over && styles.dropZoneOver)}>
                    {over
                      ? 'Suelta aquí'
                      : open
                        ? 'Arrastra una tarjeta'
                        : 'Sin incapacidades'}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </BoardShell>

      {observing !== null && (
        <ObserveIncapacityDialog
          incapacity={observing}
          onClose={() => {
            setObserving(null);
          }}
          onDone={async () => {
            setObserving(null);
            await refresh();
          }}
        />
      )}
    </>
  );
};
